import { Injectable, Inject } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import brandInfo from "src/environments/brand.json";

/**
 * Keeps the <link rel="canonical"> tag in sync with the current route.
 *
 * The server (server/helpers/inject-meta.js) injects a canonical link into the
 * HTML shell for the initial page load so crawlers see it immediately. Angular
 * router navigations, however, don't reload the shell, so the canonical would
 * otherwise stay stuck on the first URL. This service updates it client-side on
 * each navigation, mirroring the server's URL rules (canonicalHost + path,
 * query/hash stripped, trailing slash enforced).
 *
 * canonicalHost comes from brand.json, which is already environment-resolved at
 * build time (www.* -> dev.* for non-prod), so this stays consistent with both
 * the server injection and sitemap.xml.
 */
@Injectable({ providedIn: "root" })
export class CanonicalService {
  private readonly canonicalHost: string = (brandInfo as { canonicalHost?: string }).canonicalHost || "";

  constructor(@Inject(DOCUMENT) private document: Document) {}

  /**
   * Updates (or creates) the canonical link for the given path.
   * @param pathname Route path. Defaults to the current location pathname.
   */
  updateCanonicalUrl(pathname?: string): void {
    const url = this.buildCanonicalUrl(
      pathname != null ? pathname : this.document.location.pathname
    );
    if (!url) return;

    const head = this.document.head;
    if (!head) return;

    let link = head.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement("link");
      link.setAttribute("rel", "canonical");
      head.appendChild(link);
    }
    link.setAttribute("href", url);
  }

  /** Mirrors server buildCanonicalUrl(): strip query/hash, collapse slashes, trailing slash. */
  private buildCanonicalUrl(pathname: string): string | null {
    const base = String(this.canonicalHost || "").replace(/\/+$/g, "");
    if (!base) return null;

    let cleanPath = String(pathname == null ? "/" : pathname)
      .split("?")[0]
      .split("#")[0];

    if (!cleanPath.startsWith("/")) cleanPath = `/${cleanPath}`;
    cleanPath = cleanPath.replace(/\/{2,}/g, "/");

    if (cleanPath === "/") return `${base}/`;

    const url = `${base}${cleanPath}`;
    return url.endsWith("/") ? url : `${url}/`;
  }
}
